import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, ModalBody, Tooltip } from "reactstrap";
import CustomButton from "../../components/AuthenticationComponents/CustomButton";
import SecondaryButton from "../../components/Common/SecondaryButton";
import { get, post } from "../../api/axios";
import endpoints from "../../api/endpoints";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import arrowRightIcon from "../../assets/images/user-dashboard/orange-arrow-right.svg";
import arrowRightIconAuth from "../../assets/images/authentication/arrow-right.svg";
import star from "../../assets/images/user-dashboard/star-filled.svg";
import starOutline from "../../assets/images/user-dashboard/star-unfilled.svg";
import map from "../../assets/images/user-dashboard/map.svg";
import clock from "../../assets/images/user-dashboard/clock.svg";
import calendar from "../../assets/images/user-dashboard/calendar.svg";
import fallbackImage from "../../assets/images/user-dashboard/image.svg";
import closeIcon from "../../assets/images/user-dashboard/remove-circle.svg";
import tickIcon from "../../assets/images/support/tick-circle-white.svg";

import "../../assets/scss/custom/components/restaurant-detail-modal.scss";

/* -------------------------------------------------------------------------
 * Sub-components
 * ---------------------------------------------------------------------- */

const StarRatingInput = ({ value, onChange }) => (
  <div className="rdm-stars" role="radiogroup" aria-label="Your rating">
    {[1, 2, 3, 4, 5].map((starValue) => {
      const filled = starValue <= value;
      return (
        <button
          key={starValue}
          type="button"
          className="rdm-stars__btn"
          role="radio"
          aria-checked={filled}
          aria-label={`${starValue} star${starValue > 1 ? "s" : ""}`}
          onClick={() => onChange(starValue)}
        >
          <img src={filled ? star : starOutline} alt="" />
        </button>
      );
    })}
  </div>
);

StarRatingInput.propTypes = {
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
};

/* -------------------------------------------------------------------------
 * Main component — full-width bottom sheet built on reactstrap's
 * <Modal>/<ModalBody>, the same modal primitive used everywhere else in
 * the Minible theme. The sheet is re-anchored to the bottom edge of the
 * viewport and stretched edge-to-edge purely via CSS on the dialog/wrapper
 * — open/close mechanics, ESC, backdrop-click, and body-scroll-lock all
 * still come from reactstrap. Inner content stays centered in a max-width
 * column so the sheet itself reads full-bleed while text/image don't
 * stretch uncomfortably wide on large screens.
 * ---------------------------------------------------------------------- */

const RestaurantDetailModal = ({
  restaurant = null,
  isOpen,
  onClose,
  onRedeem = () => {},
  onRatingChange = () => {},
  onSaveNote = () => {},
  onRefreshRestaurant = () => {},
}) => {
  const [noteValue, setNoteValue] = useState("");
  const [ratingValue, setRatingValue] = useState(0);
  const [aggregateRating, setAggregateRating] = useState(0);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const startYRef = useRef(0);

  const toggleTooltip = () => setTooltipOpen(!tooltipOpen);

  // Configure toastr options on mount
  useEffect(() => {
    toastr.options = {
      positionClass: "toast-bottom-left",
      timeOut: 5000,
      closeButton: true,
      progressBar: true,
      newestOnTop: true,
      preventDuplicates: true,
      extendedTimeOut: 1000,
      tapToDismiss: false,
    };
  }, []);

  // Calculate distance using Haversine formula
  const calculateDistance = useCallback((lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return null;

    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * (Math.PI / 180)) *
        Math.cos(lat2 * (Math.PI / 180)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    return distance.toFixed(1);
  }, []);

  // Load user location from localStorage
  useEffect(() => {
    const storedLocation = localStorage.getItem("userLocation");
    if (storedLocation) {
      setUserLocation(JSON.parse(storedLocation));
    }
  }, []);

  // Sync aggregate rating with restaurant prop
  useEffect(() => {
    if (restaurant) {
      setAggregateRating(restaurant.rating || 0);
    }
  }, [restaurant]);

  // Drag handlers
  const handleDragStart = useCallback((clientY) => {
    startYRef.current = clientY;
    setIsDragging(true);
  }, []);

  const handleDragMove = useCallback((clientY) => {
    const deltaY = clientY - startYRef.current;
    if (deltaY > 0) {
      setDragY(deltaY);
    }
  }, []);

  const handleDragEnd = useCallback(() => {
    if (dragY > 100) {
      onClose();
    }
    setDragY(0);
    setIsDragging(false);
    startYRef.current = 0;
  }, [dragY, onClose]);

  const handleMouseDown = useCallback(
    (e) => {
      handleDragStart(e.clientY);
    },
    [handleDragStart],
  );

  const handleMouseMove = useCallback(
    (e) => {
      handleDragMove(e.clientY);
    },
    [handleDragMove],
  );

  const handleMouseUp = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  const handleTouchStart = useCallback(
    (e) => {
      handleDragStart(e.touches[0].clientY);
    },
    [handleDragStart],
  );

  const handleTouchMove = useCallback(
    (e) => {
      handleDragMove(e.touches[0].clientY);
    },
    [handleDragMove],
  );

  const handleTouchEnd = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  const handleDragHandleClick = useCallback(() => {
    onClose();
  }, [onClose]);

  // Add/remove event listeners
  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      window.addEventListener("touchmove", handleTouchMove);
      window.addEventListener("touchend", handleTouchEnd);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [
    isDragging,
    handleMouseMove,
    handleMouseUp,
    handleTouchMove,
    handleTouchEnd,
  ]);

  // Reset local edit state whenever a different restaurant is opened
  useEffect(() => {
    if (restaurant) {
      setNoteValue(restaurant.privateNote || "");
      setRatingValue(restaurant.userRating || 0);

      // Fetch user's saved rating and note from API
      const fetchUserRatingAndNote = async () => {
        try {
          const ratingUrl = endpoints.restaurantRating.replace(
            "{{restaurant_id}}",
            restaurant.id,
          );
          const noteUrl = endpoints.restaurantPersonalNote.replace(
            "{{restaurant_id}}",
            restaurant.id,
          );

          const [ratingResponse, noteResponse] = await Promise.all([
            get(ratingUrl),
            get(noteUrl),
          ]);

          if (ratingResponse.data?.success && ratingResponse.data.data) {
            setRatingValue(ratingResponse.data.data.rating || 0);
          }

          if (noteResponse.data?.success && noteResponse.data.data) {
            setNoteValue(noteResponse.data.data.note || "");
          }
        } catch (error) {
          console.error("Failed to fetch user rating and note:", error);
          // Keep the default values from restaurant prop if API fails
        }
      };

      fetchUserRatingAndNote();
    }
  }, [restaurant]);

  if (!restaurant) return null;

  const {
    id,
    name,
    image,
    category,
    categories = [category],
    tag,
    distance: propDistance,
    latitude,
    longitude,
    description,
    hoursLabel = "Today",
    hoursValue,
    visitHistory,
    address,
    redeemLabel = "Redeem Voucher",
  } = restaurant;

  // Validate image URL
  const isValidImageUrl = (url) => {
    if (!url) return false;
    if (url.includes("encrypted-tbn0.gstatic.com") && url.includes("q=tbn:")) {
      return false;
    }
    return true;
  };

  const displayImage = isValidImageUrl(image) ? image : fallbackImage;

  // Calculate distance if user location is available
  const calculatedDistance =
    userLocation && latitude && longitude
      ? `${calculateDistance(userLocation.lat, userLocation.lng, latitude, longitude)} mi`
      : propDistance || null;

  // Handle multiple categories display
  const displayCategories = categories.slice(0, 3);
  const hasMoreCategories = categories.length > 3;
  const additionalCount = hasMoreCategories ? categories.length - 3 : 0;
  const allCategories = categories.join(", ");

  const handleStarClick = async (starValue) => {
    setRatingValue(starValue);
    try {
      const url = endpoints.restaurantRating.replace("{{restaurant_id}}", id);
      const response = await post(url, { rating: starValue });

      // Update aggregate rating from response
      if (response.data && response.data.data) {
        const newAggregateRating =
          response.data.data.average_rating || response.data.data.rating;
        if (newAggregateRating !== undefined) {
          setAggregateRating(newAggregateRating);
        }
      }

      // Notify parent to update the restaurant list with user's rating
      onRatingChange(id, starValue);

      // Refresh restaurant data to get updated aggregate rating
      if (onRefreshRestaurant) {
        onRefreshRestaurant(id);
      }

      toastr.success("Rating saved successfully");
    } catch (error) {
      console.error("Failed to save rating:", error);
      toastr.error("Failed to save rating");
      // Revert the UI state if the API call fails
      setRatingValue(restaurant.userRating || 0);
    }
  };

  const handleSaveNote = async () => {
    setIsSavingNote(true);
    try {
      const url = endpoints.restaurantPersonalNote.replace(
        "{{restaurant_id}}",
        id,
      );
      await post(url, { note: noteValue });
      onSaveNote(id, noteValue);
      toastr.success("Note saved successfully");
    } catch (error) {
      console.error("Failed to save note:", error);
      toastr.error("Failed to save note");
    } finally {
      setIsSavingNote(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      modalClassName="rdm-sheet-wrapper"
      className="rdm-modal-dialog"
      contentClassName="rdm-modal-content"
      backdropClassName="rdm-modal-backdrop"
      labelledBy="rdm-title"
    >
      <ModalBody className="rdm-modal-body">
        <button
          type="button"
          className="rdm-close-button"
          onClick={onClose}
          aria-label="Close modal"
        >
          <img src={closeIcon} alt="Close" />
        </button>
        <div
          className="rdm-drag-handle-row"
          onClick={handleDragHandleClick}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          <div
            className="rdm-drag-handle"
            style={{
              transform: `translateY(${dragY}px)`,
              cursor: isDragging ? "grabbing" : "pointer",
            }}
          />
        </div>

        <div className="rdm-inner">
          <div className="rdm-media">
            <img
              src={displayImage}
              alt={name}
              onError={(e) => {
                e.target.src = fallbackImage;
              }}
            />
          </div>

          <div className="rdm-top-row">
            <div className="rdm-badges">
              {displayCategories.map((cat, index) => (
                <span key={index} className="rdm-badge rdm-badge--category">
                  {cat}
                </span>
              ))}
              {hasMoreCategories && (
                <>
                  <span
                    className="rdm-badge rdm-badge--more"
                    id={`category-tooltip-${id}`}
                    onClick={toggleTooltip}
                  >
                    +{additionalCount} more
                  </span>
                  <Tooltip
                    placement="bottom"
                    isOpen={tooltipOpen}
                    target={`category-tooltip-${id}`}
                    toggle={toggleTooltip}
                    innerClassName="rdm-tooltip-inner"
                    className="rdm-tooltip"
                    transition={{ timeout: 0 }}
                  >
                    {allCategories}
                  </Tooltip>
                </>
              )}
              {tag && <span className="rdm-badge rdm-badge--tag">{tag}</span>}
            </div>
          </div>

          <div className="rdm-title-row">
            <h2 className="rdm-title" id="rdm-title">
              {name}
            </h2>
            <span className="rdm-meta">
              <img src={star} alt="" />
              {aggregateRating.toFixed(1)}
              <span className="rdm-meta__dot">&bull;</span>
              {calculatedDistance && (
                <>
                  <img src={map} alt="" />
                  {calculatedDistance}
                </>
              )}
            </span>
          </div>

          <p className="rdm-description">{description}</p>

          <div className="rdm-hours">
            <img src={clock} alt="" />
            <span className="rdm-hours__label">{hoursLabel}:</span>
            <span className="rdm-hours__value">{hoursValue}</span>
          </div>

          {address && (
            <>
              <p className="rdm-section-label">Address</p>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="rdm-address-link"
              >
                <div className="rdm-address-row">
                  <img src={map} alt="Map" />
                  <span className="rdm-address-text">{address}</span>
                  <img src={arrowRightIcon} alt="Open in Maps" />
                </div>
              </a>
            </>
          )}

          {visitHistory && (
            <>
              <p className="rdm-section-label">Visit History</p>
              <div className="rdm-info-row">
                <span className="rdm-info-row__left">
                  <img src={calendar} alt="" />
                  <span className="rdm-info-row__label">Last visited:</span>
                  <span className="rdm-info-row__value">
                    {visitHistory.lastVisitedLabel}
                  </span>
                </span>
                <span className="rdm-info-row__badge">
                  {visitHistory.visitCount} Visit
                  {visitHistory.visitCount === 1 ? "" : "s"}
                </span>
              </div>
            </>
          )}

          <p className="rdm-section-label">Your Rating</p>
          <div className="rdm-info-row">
            <span className="rdm-info-row__label rdm-info-row__label--muted">
              Tap stars to update
            </span>
            <span className="rdm-rating-control">
              <span className="rdm-rating-control__value">
                {ratingValue.toFixed(1)}
              </span>
              <StarRatingInput value={ratingValue} onChange={handleStarClick} />
            </span>
          </div>

          <p className="rdm-section-label">Private Note</p>
          <textarea
            className="rdm-note-input"
            rows={3}
            placeholder="Add a private note about this place..."
            value={noteValue}
            onChange={(e) => setNoteValue(e.target.value)}
          />

          <div className="rdm-save-row">
            <SecondaryButton
              type="button"
              variant="orange"
              onClick={handleSaveNote}
              icon={tickIcon}
              iconPosition="right"
              disabled={isSavingNote || !noteValue.trim()}
              className="rdm-save-btn"
            >
              {isSavingNote ? "Saving..." : "Save"}
            </SecondaryButton>
          </div>

          <CustomButton
            type="button"
            variant="primary"
            iconPosition="right"
            icon={arrowRightIconAuth}
            className="w-100"
            onClick={() => onRedeem(restaurant)}
          >
            {redeemLabel}
          </CustomButton>
        </div>
      </ModalBody>
    </Modal>
  );
};

RestaurantDetailModal.propTypes = {
  restaurant: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    category: PropTypes.string,
    categories: PropTypes.arrayOf(PropTypes.string),
    tag: PropTypes.string,
    rating: PropTypes.number.isRequired,
    distance: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    hoursLabel: PropTypes.string,
    hoursValue: PropTypes.string.isRequired,
    visitHistory: PropTypes.shape({
      lastVisitedLabel: PropTypes.string.isRequired,
      visitCount: PropTypes.number.isRequired,
    }),
    userRating: PropTypes.number,
    privateNote: PropTypes.string,
    address: PropTypes.string,
    redeemLabel: PropTypes.string,
  }),
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRedeem: PropTypes.func,
  onRatingChange: PropTypes.func,
  onSaveNote: PropTypes.func,
  onRefreshRestaurant: PropTypes.func,
};

export default RestaurantDetailModal;
